import type { BillingClientSummary } from "@school-sales-ai/shared";
import { useCancelSubscription, useResumeSubscription } from "@/hooks/useBilling";
import { formatMxn } from "@/lib/format-mxn";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

type SubscriptionModalProps = {
  client: BillingClientSummary;
  onClose: () => void;
};

const DATE_FORMAT = new Intl.DateTimeFormat("es-MX", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatLong(iso: string | null): string {
  return iso ? DATE_FORMAT.format(new Date(iso)) : "el final del periodo";
}

/**
 * Cancelling and un-cancelling a school's subscription.
 *
 * Cancellation always means "at period end". The school keeps the service it paid
 * for, and the action stays reversible for as long as the period runs. Immediate
 * cancellation is deliberately absent — it cannot be undone, and the Stripe
 * dashboard is the right place for that rare case.
 */
export function SubscriptionModal({
  client,
  onClose,
}: SubscriptionModalProps): React.JSX.Element {
  const cancel = useCancelSubscription();
  const resume = useResumeSubscription();
  const pending = Boolean(client.cancelAt);

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {pending ? "Reactivar suscripción" : "Cancelar suscripción"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                <strong>{client.clientName}</strong> —{" "}
                {formatMxn(client.currentPriceAmountCents, client.currentPriceCurrency)} + IVA
              </p>
              {pending ? (
                <p>
                  Hoy está programada para terminar el{" "}
                  <strong>{formatLong(client.cancelAt)}</strong>. Reactivarla quita esa
                  fecha y el cobro sigue como siempre.
                </p>
              ) : (
                <>
                  <p>
                    El servicio sigue funcionando hasta el{" "}
                    <strong>{formatLong(client.currentPeriodEnd)}</strong>, que es el
                    periodo ya pagado. Después de esa fecha no se vuelve a cobrar.
                  </p>
                  <p className="text-ink-500">
                    Se puede deshacer en cualquier momento antes de esa fecha. Esto{" "}
                    <strong>no</strong> apaga el servicio de la escuela en ScholaOps —
                    eso se hace aparte, en Clientes.
                  </p>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Volver</AlertDialogCancel>
          {pending ? (
            <AlertDialogAction
              disabled={resume.isPending}
              onClick={() => resume.mutate(client.clientId, { onSuccess: onClose })}
            >
              {resume.isPending ? "Reactivando…" : "Reactivar"}
            </AlertDialogAction>
          ) : (
            <AlertDialogAction
              disabled={cancel.isPending}
              className="bg-[oklch(0.55_0.18_16)] hover:bg-[oklch(0.48_0.18_16)]"
              onClick={() => cancel.mutate(client.clientId, { onSuccess: onClose })}
            >
              {cancel.isPending ? "Cancelando…" : "Sí, cancelar"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
